/**
 * DesignQuestionnaireFlow - GB組織74問・7パート質問回答フロー (gb-org-questionnaire-74)
 * モック_GB組織_回答画面_v1.html (s0〜s9) 準拠。スマホ縦持ちファースト・GB緑系配色。
 * 画面向きによる出し分けは行わない (旧130問デザインの横画面強制は廃止)。
 *
 * formatType別UI: binary→2択カード, scale→ScaleQuestionUI,
 * choice→ChoiceQuestionUI, matrix→MatrixQuestionUI (部門×4段階)
 *
 * 出題順・選択肢配置は質問マスタの order で固定 (Part1のカウンターバランスは
 * CSV作成時に焼き込み済み)。ランダム化は行わない。
 * Part3→Part4 間のみ ResetScreen (モックs4) を表示する。
 * Part6 (matrix) は自部門以外の全部門を行として動的生成し、部門0件時はスキップする。
 */
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { fetchAuthSession } from 'aws-amplify/auth';
import { QuestionSet, UserAnswer, SubmitAnswerResponse, MyDepartmentsResponse } from '../types';
import type { ProgressAnswer } from '../../../types';
import { useIntegratedSession } from '../hooks/useIntegratedSession';
import { LanguageSelector } from './LanguageSelector';
import { ScaleQuestionUI } from './ScaleQuestionUI';
import { ChoiceQuestionUI } from './ChoiceQuestionUI';
import { MatrixQuestionUI } from './MatrixQuestionUI';
import { NameGeneratorUI, type Colleague } from './NameGeneratorUI';
import { getPart5Mode } from '../config/part5Mode';
import { ResetScreen } from './ResetScreen';
import { fetchMyDepartments, fetchMyColleagues, saveConsultSelections } from '../api/questionnaireApi';
import { getPartNumber, PART_CONFIG, ESTIMATED_TIME_MINUTES } from '../../../constants/partConfig';
import './DesignQuestionnaireFlow.scss';

/**
 * 言語に応じたテキストを取得するヘルパー関数（10言語対応）
 */
const getLocalizedText = (
  item: { text: string; textEn?: string; textId?: string; textMn?: string; textNe?: string; textVi?: string; textAr?: string; textBn?: string; textZh?: string; textEs?: string },
  lang: string
): string => {
  switch (lang) {
    case 'en': return item.textEn || item.text;
    case 'id': return item.textId || item.text;
    case 'mn': return item.textMn || item.text;
    case 'ne': return item.textNe || item.text;
    case 'vi': return item.textVi || item.text;
    case 'ar': return item.textAr || item.text;
    case 'bn': return item.textBn || item.text;
    case 'zh': return item.textZh || item.text;
    case 'es': return item.textEs || item.text;
    default: return item.text;
  }
};

// パート別リード文 (モック s2/s3/s5/s8 の .q-text)
const SCALE_LEAD_TEXTS: Record<number, string> = {
  2: '先月の1か月を振り返って、あなた自身に当てはまるものを選んでください。',
  3: '先月の1か月を振り返って、あなた自身に当てはまるものを選んでください。',
  4: '先月の1か月を振り返ってください。',
  7: 'あなた自身に当てはまるものを選んでください。',
};

type FlowState = 'start' | 'continue' | 'answering' | 'reset' | 'completed';

interface DesignQuestionnaireFlowProps {
  questionSet: QuestionSet;
  onSubmit: (answers: UserAnswer[]) => Promise<SubmitAnswerResponse>;
  onNavigateToService: () => void;
  onNavigateToMyPage: () => void;
  isLoading?: boolean;
  error?: string | null;
}

export const DesignQuestionnaireFlow: React.FC<DesignQuestionnaireFlowProps> = ({
  questionSet,
  onSubmit,
  onNavigateToService,
  onNavigateToMyPage,
  isLoading = false,
  error = null,
}) => {
  const { t, i18n } = useTranslation();
  const [flowState, setFlowState] = useState<FlowState>('start');
  const [answers, setAnswers] = useState<UserAnswer[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [pendingNextIndex, setPendingNextIndex] = useState<number | null>(null);

  // Part6 (部門間マトリクス) 用: 自部門以外の部門一覧
  const [departments, setDepartments] = useState<MyDepartmentsResponse['departments']>([]);
  const [departmentsLoaded, setDepartmentsLoaded] = useState(false);
  // 現在表示中のmatrix質問の回答 (departmentId → 1〜4)
  const [matrixValues, setMatrixValues] = useState<Record<string, number>>({});

  // Part5 ネームジェネレータ用: 所属部署内の同僚と選択結果 (getPart5Mode()==='namegen' のとき使用)
  // 同僚一覧は GET /my/colleagues で取得（下の useEffect）。
  const [namegenColleagues, setNamegenColleagues] = useState<Colleague[]>([]);
  const [namegenSelectedIds, setNamegenSelectedIds] = useState<string[]>([]);
  // 導入画面: 「上記を確認のうえ、回答を始めます」チェックで回答開始ボタンを有効化する。
  const [agreedToStart, setAgreedToStart] = useState(false);

  // 出題順は order で固定 (ランダム化なし)
  const sortedQuestions = useMemo(
    () => [...questionSet.questions].sort((a, b) => a.order - b.order),
    [questionSet.questions]
  );

  const partNumberOf = useCallback((index: number): number => {
    return sortedQuestions[index]?.partNumber ?? getPartNumber(index);
  }, [sortedQuestions]);

  const matrixQuestionIndex = useMemo(
    () => sortedQuestions.findIndex(q => q.formatType === 'matrix'),
    [sortedQuestions]
  );

  // 部門0件時はPart6 (matrix) をスキップする
  const isSkippedIndex = useCallback((index: number): boolean => {
    return sortedQuestions[index]?.formatType === 'matrix' && departments.length === 0;
  }, [sortedQuestions, departments.length]);

  // 進捗カウンター: matrix画面は部門1行=1問分として数える
  // 分母 = (マスタ質問数 - matrix 1問) + 部門数。部門0件時はmatrixスキップで71
  const deptCount = departments.length;
  const totalDisplayQuestions = matrixQuestionIndex >= 0
    ? (sortedQuestions.length - 1) + deptCount
    : sortedQuestions.length;
  const getDisplayNumber = useCallback((index: number): number => {
    if (matrixQuestionIndex < 0 || index < matrixQuestionIndex) return index + 1;
    if (index === matrixQuestionIndex) return matrixQuestionIndex + 1;
    return deptCount > 0 ? index + deptCount : index;
  }, [matrixQuestionIndex, deptCount]);

  // 統合セッション管理
  const {
    hasProgress,
    progress,
    isLoading: sessionLoading,
    error: sessionError,
    saveProgress,
    clearProgress,
  } = useIntegratedSession({
    questionSetId: questionSet.id,
    autoCleanup: true,
    debugMode: process.env.NODE_ENV === 'development',
  });

  // Part6用の部門一覧を取得 (取得失敗時はPart6をスキップして回答は継続可能にする)
  useEffect(() => {
    let cancelled = false;
    fetchMyDepartments()
      .then((res) => {
        if (!cancelled) {
          setDepartments(res.departments);
          setDepartmentsLoaded(true);
        }
      })
      .catch((err) => {
        console.warn('[DesignQuestionnaireFlow] Failed to fetch departments, Part6 will be skipped:', err);
        if (!cancelled) {
          setDepartments([]);
          setDepartmentsLoaded(true);
        }
      });
    return () => { cancelled = true; };
  }, []);

  // Part5 ネームジェネレータ用の同僚一覧を取得 (namegen モードのみ。取得失敗時は空=選択不可表示)
  useEffect(() => {
    if (getPart5Mode() !== 'namegen') return;
    let cancelled = false;
    fetchMyColleagues()
      .then((res) => {
        if (!cancelled) setNamegenColleagues(res.colleagues ?? []);
      })
      .catch((err) => {
        console.warn('[DesignQuestionnaireFlow] Failed to fetch colleagues:', err);
        if (!cancelled) setNamegenColleagues([]);
      });
    return () => { cancelled = true; };
  }, []);

  // 初期化時に保存された進捗をチェック
  const hasInitializedFlow = useRef(false);

  // 回答中にCognitoトークンを定期リフレッシュ（期限切れ防止）
  useEffect(() => {
    if (flowState !== 'answering' && flowState !== 'reset') return;

    const REFRESH_INTERVAL_MS = 25 * 60 * 1000; // 25 minutes
    const intervalId = setInterval(async () => {
      try {
        await fetchAuthSession({ forceRefresh: true });
        console.log('[DesignQuestionnaireFlow] Token refreshed proactively');
      } catch (err) {
        console.warn('[DesignQuestionnaireFlow] Token refresh failed:', err);
      }
    }, REFRESH_INTERVAL_MS);

    return () => clearInterval(intervalId);
  }, [flowState]);

  useEffect(() => {
    if (!sessionLoading && !hasInitializedFlow.current) {
      hasInitializedFlow.current = true;
      if (hasProgress && progress && Object.keys(progress.answers).length > 0) {
        setFlowState('continue');
      } else {
        setFlowState('start');
      }
    }
  }, [hasProgress, progress, sessionLoading]);

  // matrix質問に入ったとき、既存回答から選択状態を復元
  useEffect(() => {
    const q = sortedQuestions[currentQuestionIndex];
    if (q?.formatType === 'matrix') {
      const values: Record<string, number> = {};
      answers
        .filter(a => a.questionId === q.id && a.departmentId && a.scaleValue != null)
        .forEach(a => { values[a.departmentId!] = a.scaleValue!; });
      setMatrixValues(values);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentQuestionIndex]);

  /**
   * UserAnswer[] を進捗保存用のマップ (マスタ質問インデックス → ProgressAnswer) に変換
   */
  const buildAnswersMap = useCallback((userAnswers: UserAnswer[]): Record<number, ProgressAnswer> => {
    const map: Record<number, ProgressAnswer> = {};
    sortedQuestions.forEach((q, idx) => {
      const qAnswers = userAnswers.filter(a => a.questionId === q.id);
      if (qAnswers.length === 0) return;
      if (q.formatType === 'matrix') {
        const values: Record<string, number> = {};
        qAnswers.forEach(a => {
          if (a.departmentId && a.scaleValue != null) values[a.departmentId] = a.scaleValue;
        });
        if (Object.keys(values).length > 0) map[idx] = { type: 'matrix', values };
      } else if (qAnswers[0].scaleValue !== undefined) {
        map[idx] = { type: 'scale', scaleValue: qAnswers[0].scaleValue };
      } else if (qAnswers[0].answerChoiceId) {
        map[idx] = { type: 'choice', answerChoiceId: qAnswers[0].answerChoiceId };
      }
    });
    return map;
  }, [sortedQuestions]);

  // 質問開始
  const handleStartQuestionnaire = useCallback(() => {
    setAnswers([]);
    setCurrentQuestionIndex(0);
    setMatrixValues({});
    setFlowState('answering');
  }, []);

  // 続きから回答
  const handleContinueFromSaved = useCallback(() => {
    if (!progress) return;
    const now = new Date().toISOString();
    const restoredAnswers: UserAnswer[] = [];
    Object.entries(progress.answers).forEach(([indexStr, answer]) => {
      const qIndex = parseInt(indexStr, 10);
      const question = sortedQuestions[qIndex];
      if (!question) return;
      if (typeof answer === 'object' && answer !== null && 'type' in answer) {
        if (answer.type === 'scale') {
          restoredAnswers.push({ questionId: question.id, scaleValue: answer.scaleValue, answeredAt: now });
        } else if (answer.type === 'matrix') {
          Object.entries(answer.values).forEach(([departmentId, value]) => {
            restoredAnswers.push({ questionId: question.id, scaleValue: value, departmentId, answeredAt: now });
          });
        } else {
          restoredAnswers.push({ questionId: question.id, answerChoiceId: answer.answerChoiceId, answeredAt: now });
        }
      } else {
        // 旧形式: string（answerChoiceId）
        restoredAnswers.push({ questionId: question.id, answerChoiceId: answer as string, answeredAt: now });
      }
    });
    setAnswers(restoredAnswers);
    setCurrentQuestionIndex(Math.min(progress.currentQuestionIndex, sortedQuestions.length - 1));
    setFlowState('answering');
  }, [progress, sortedQuestions]);

  // 最初から回答
  const handleStartFromBeginning = useCallback(() => {
    clearProgress();
    setAnswers([]);
    setCurrentQuestionIndex(0);
    setMatrixValues({});
    setFlowState('answering');
  }, [clearProgress]);

  // 送信エラー状態（advanceToNextより前に定義）
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [failedAnswers, setFailedAnswers] = useState<UserAnswer[] | null>(null);

  // 回答送信（advanceToNextより前に定義）
  const handleSubmit = useCallback(async (finalAnswers: UserAnswer[]) => {
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      console.log('[DesignQuestionnaireFlow] Submitting answers:', finalAnswers.length);
      await onSubmit(finalAnswers);
      console.log('[DesignQuestionnaireFlow] Submit success');
      // §03レベルB: ネームジェネレータで選んだ相談相手を保存 (best-effort・完了は妨げない)
      if (getPart5Mode() === 'namegen' && namegenSelectedIds.length > 0) {
        try {
          await saveConsultSelections(namegenSelectedIds);
        } catch (e) {
          console.warn('[DesignQuestionnaireFlow] Failed to save consult selections:', e);
        }
      }
      clearProgress();
      setFlowState('completed');
    } catch (err: any) {
      console.error('[DesignQuestionnaireFlow] Submit error:', err);
      const message = err?.response?.data?.message || err?.message || '回答の送信に失敗しました';
      // SESSION_ALREADY_COMPLETED の場合は完了画面を表示
      if (err?.response?.data?.error === 'SESSION_ALREADY_COMPLETED' || message.includes('already been completed')) {
        clearProgress();
        setFlowState('completed');
        return;
      }
      setSubmitError(message);
      setFailedAnswers(finalAnswers);
    } finally {
      setIsSubmitting(false);
    }
  }, [onSubmit, clearProgress, namegenSelectedIds]);

  /**
   * 次の質問へ進む共通ロジック
   * - 部門0件時はmatrix質問をスキップ
   * - Part3→Part4遷移時のみResetScreen (モックs4) を表示
   */
  const advanceToNext = useCallback((newAnswers: UserAnswer[], nextIndexRaw: number) => {
    let nextIndex = nextIndexRaw;
    while (nextIndex < sortedQuestions.length && isSkippedIndex(nextIndex)) {
      nextIndex++;
    }

    if (nextIndex >= sortedQuestions.length) {
      // 全質問完了
      handleSubmit(newAnswers);
      return;
    }

    const prevPart = partNumberOf(currentQuestionIndex);
    const nextPart = partNumberOf(nextIndex);
    if (prevPart !== nextPart && PART_CONFIG[nextPart]?.resetBefore) {
      // Part3→Part4: 心理的リセット中間画面
      setPendingNextIndex(nextIndex);
      setFlowState('reset');
    } else {
      setCurrentQuestionIndex(nextIndex);
    }
  }, [sortedQuestions.length, isSkippedIndex, partNumberOf, currentQuestionIndex, handleSubmit]);

  // リセット画面確認後
  const handleResetConfirm = useCallback(() => {
    if (pendingNextIndex !== null) {
      setCurrentQuestionIndex(pendingNextIndex);
      setPendingNextIndex(null);
      setFlowState('answering');
    }
  }, [pendingNextIndex]);

  // binary/choice回答処理
  const handleChoiceAnswer = useCallback((choiceId: string) => {
    if (isSubmitting) return;
    const currentQ = sortedQuestions[currentQuestionIndex];
    const userAnswer: UserAnswer = {
      questionId: currentQ.id,
      answerChoiceId: choiceId,
      answeredAt: new Date().toISOString(),
    };
    const newAnswers = [...answers];
    const existingIdx = newAnswers.findIndex(a => a.questionId === userAnswer.questionId);
    if (existingIdx >= 0) { newAnswers[existingIdx] = userAnswer; } else { newAnswers.push(userAnswer); }
    setAnswers(newAnswers);
    saveProgress(buildAnswersMap(newAnswers), currentQuestionIndex);

    setTimeout(() => {
      advanceToNext(newAnswers, currentQuestionIndex + 1);
    }, 300);
  }, [isSubmitting, sortedQuestions, currentQuestionIndex, answers, saveProgress, buildAnswersMap, advanceToNext]);

  // scale回答処理
  const handleScaleAnswer = useCallback((scaleValue: number) => {
    if (isSubmitting) return;
    const currentQ = sortedQuestions[currentQuestionIndex];
    const userAnswer: UserAnswer = {
      questionId: currentQ.id,
      scaleValue,
      answeredAt: new Date().toISOString(),
    };
    const newAnswers = [...answers];
    const existingIdx = newAnswers.findIndex(a => a.questionId === userAnswer.questionId);
    if (existingIdx >= 0) { newAnswers[existingIdx] = userAnswer; } else { newAnswers.push(userAnswer); }
    setAnswers(newAnswers);
    saveProgress(buildAnswersMap(newAnswers), currentQuestionIndex);

    setTimeout(() => {
      advanceToNext(newAnswers, currentQuestionIndex + 1);
    }, 300);
  }, [isSubmitting, sortedQuestions, currentQuestionIndex, answers, saveProgress, buildAnswersMap, advanceToNext]);

  // matrix回答処理: 部門毎の選択 (次へボタンで確定)
  const handleMatrixChange = useCallback((departmentId: string, value: number) => {
    if (isSubmitting) return;
    setMatrixValues(prev => ({ ...prev, [departmentId]: value }));
  }, [isSubmitting]);

  // matrix確定: 部門毎に1エントリのUserAnswerへ展開して次へ
  const handleMatrixNext = useCallback(() => {
    if (isSubmitting) return;
    const currentQ = sortedQuestions[currentQuestionIndex];
    const now = new Date().toISOString();
    const matrixAnswers: UserAnswer[] = departments
      .filter(d => matrixValues[d.id] != null)
      .map(d => ({
        questionId: currentQ.id,
        scaleValue: matrixValues[d.id],
        departmentId: d.id,
        answeredAt: now,
      }));
    // 同一質問の既存エントリを差し替え
    const newAnswers = [
      ...answers.filter(a => a.questionId !== currentQ.id),
      ...matrixAnswers,
    ];
    setAnswers(newAnswers);
    saveProgress(buildAnswersMap(newAnswers), currentQuestionIndex);
    advanceToNext(newAnswers, currentQuestionIndex + 1);
  }, [isSubmitting, sortedQuestions, currentQuestionIndex, departments, matrixValues, answers, saveProgress, buildAnswersMap, advanceToNext]);

  // Part5 ネームジェネレータ「次へ」: 現状は前進のみ。
  // TODO(backend/data-model): 選択した同僚ID(namegenSelectedIds)を保存するには
  // UserAnswer に targetUserId 相当のフィールド追加 or 専用テーブルが必要（§03レベルB入力）。
  // ネームジェネレータ「次へ」: 選択人数から O-11(相談人数)の回答を導出して送信し前進する。
  // これにより完了時の全問回答チェックを満たす(選んだ同僚IDの保存は完了時に別途 POST)。
  //   選択0名→[0]0人 / 1名→[1]1人 / 2名以上→[2]2〜3人
  const handleNamegenNext = useCallback(() => {
    if (isSubmitting) return;
    const currentQ = sortedQuestions[currentQuestionIndex];
    const count = namegenSelectedIds.length;
    const targetOrder = count === 0 ? 0 : count === 1 ? 1 : 2;
    const choices = [...(currentQ.answerChoices ?? [])].sort((a, b) => a.order - b.order);
    const choice = choices.find((c) => c.order === targetOrder) ?? choices[targetOrder] ?? choices[0];

    let newAnswers = answers;
    if (choice) {
      const userAnswer: UserAnswer = {
        questionId: currentQ.id,
        answerChoiceId: choice.id,
        answeredAt: new Date().toISOString(),
      };
      newAnswers = [...answers];
      const idx = newAnswers.findIndex((a) => a.questionId === userAnswer.questionId);
      if (idx >= 0) newAnswers[idx] = userAnswer;
      else newAnswers.push(userAnswer);
      setAnswers(newAnswers);
      saveProgress(buildAnswersMap(newAnswers), currentQuestionIndex);
    }
    advanceToNext(newAnswers, currentQuestionIndex + 1);
  }, [
    isSubmitting,
    sortedQuestions,
    currentQuestionIndex,
    namegenSelectedIds,
    answers,
    saveProgress,
    buildAnswersMap,
    advanceToNext,
  ]);

  // 前の質問に戻る (部門0件時はmatrixをスキップ)
  const handlePreviousQuestion = useCallback(() => {
    let prevIndex = currentQuestionIndex - 1;
    while (prevIndex >= 0 && isSkippedIndex(prevIndex)) {
      prevIndex--;
    }
    if (prevIndex >= 0) {
      setCurrentQuestionIndex(prevIndex);
    }
  }, [currentQuestionIndex, isSkippedIndex]);

  // 共通レイアウト: 中央のスマホ幅カード
  const Card = ({ children }: { children: React.ReactNode }) => (
    <div className="gb74-wrap">
      <p className="gb74-logo">
        <img src="/assets/images/logo.svg" alt="GB人財" />
      </p>
      <div className="gb74-card">
        <div className="gb74-body">{children}</div>
      </div>
    </div>
  );

  // 進捗バー (モック .progress 準拠: 緑バー + 「n / 74」カウンター + 残り時間目安)
  const ProgressHeader = ({ displayNumber }: { displayNumber: number }) => {
    const remainFrac = Math.max(0, 1 - displayNumber / totalDisplayQuestions);
    const remainMin = ESTIMATED_TIME_MINUTES * remainFrac;
    const remainText =
      remainMin >= 1 ? `あと約${Math.round(remainMin)}分` : remainMin > 0 ? 'あと約30秒' : '';
    return (
      <div className="gb74-progress">
        <div className="gb74-progress-bar">
          <div
            className="gb74-progress-fill"
            style={{ width: `${Math.round((displayNumber / totalDisplayQuestions) * 100)}%` }}
          />
        </div>
        <span className="gb74-q-section">{displayNumber} / {totalDisplayQuestions}</span>
        {remainText && (
          <span
            className="gb74-q-remain"
            style={{ marginLeft: 8, fontSize: 11, color: '#8a929b', whiteSpace: 'nowrap' }}
          >
            {remainText}
          </span>
        )}
      </div>
    );
  };

  // ローディング状態 (部門一覧の取得完了も待つ: Part6の分母計算に必要)
  if (isLoading || sessionLoading || !departmentsLoaded) {
    return (
      <Card>
        <div className="gb74-center">
          <div className="gb74-spinner" />
          <p>{t('questionnaire.loading')}</p>
        </div>
      </Card>
    );
  }

  // エラー状態
  if (error || sessionError) {
    return (
      <Card>
        <div className="gb74-center">
          <h2 className="gb74-error-title">{t('errors.genericError')}</h2>
          <p>{error || sessionError}</p>
        </div>
      </Card>
    );
  }

  // 開始画面 (モック s0) / 続きから回答画面
  if (flowState === 'start' || flowState === 'continue') {
    const isContinue = flowState === 'continue';
    return (
      <Card>
        <div className="gb74-lang">
          <LanguageSelector excludeJapanese={false} showIcon={true} compact={true} fullWidth={true} />
        </div>
        <div className="gb74-intro">
          <h2 className="gb74-intro-title">
            {t('questionnaire.introScreen.title', 'これから質問が届きます。')}
            <br />
            {t('questionnaire.introScreen.titleTime', `約${ESTIMATED_TIME_MINUTES}分で終わります。`)}
          </h2>
          <p className="gb74-intro-body">
            {t('questionnaire.introScreen.body1', '正解も不正解もありません。')}<br />
            {t('questionnaire.introScreen.body2', '感じたままに選んでください。')}<br /><br />
            {t('questionnaire.introScreen.body3', '先月の1か月を振り返って、あなた自身に当てはまるものを選んでください。')}
          </p>
          {/* 緑枠: 回答前の説明（差し替え）。1行目タイトル＋本文4行。 */}
          <div className="gb74-intro-safe">
            <strong>{t('questionnaire.introScreen.beforeStartTitle', '回答を始める前に')}</strong>
            <div style={{ marginTop: 6, whiteSpace: 'pre-line' }}>
              {t(
                'questionnaire.introScreen.beforeStartBody',
                'この質問は、ご所属の企業・団体が、よりよい組織づくりと人材育成のために実施するものです。\n回答結果はご所属先が指定した方だけが確認できます。\n回答結果は組織づくりの参考情報であり、結果だけを理由に、評価や処遇などの判断が行われることはありません。\n安心して、お答えください。'
              )}
            </div>
          </div>
          <p className="gb74-intro-note">{t('questionnaire.startScreen.autoSaveNote')}</p>
        </div>
        {/* 緑枠の下: 確認チェック。チェックで回答開始ボタンを有効化。 */}
        <label className="gb74-intro-consent">
          <input
            type="checkbox"
            checked={agreedToStart}
            onChange={(e) => setAgreedToStart(e.target.checked)}
          />
          <span>{t('questionnaire.introScreen.consentCheck', '上記を確認のうえ、回答を始めます。')}</span>
        </label>
        {isContinue ? (
          <>
            <button type="button" className="gb74-next-btn" disabled={!agreedToStart} onClick={handleContinueFromSaved}>
              {t('questionnaire.continueFromPrevious')}
            </button>
            <button type="button" className="gb74-next-btn outline" disabled={!agreedToStart} onClick={handleStartFromBeginning}>
              {t('questionnaire.startFromBeginning')}
            </button>
          </>
        ) : (
          <button type="button" className="gb74-next-btn" disabled={!agreedToStart} onClick={handleStartQuestionnaire}>
            {t('questionnaire.introScreen.understood', '回答をはじめる')}
          </button>
        )}
      </Card>
    );
  }

  // 送信中ローディング画面
  if (isSubmitting) {
    return (
      <Card>
        <div className="gb74-center">
          <div className="gb74-spinner" />
          <p>{t('questionnaire.submitting', '回答を送信中...')}</p>
        </div>
      </Card>
    );
  }

  // 心理的リセット中間画面 (モック s4: Part3→Part4)
  if (flowState === 'reset' && pendingNextIndex !== null) {
    return (
      <Card>
        <ResetScreen onConfirm={handleResetConfirm} />
      </Card>
    );
  }

  // 送信エラー画面（answering より前にチェック）
  if (submitError) {
    return (
      <Card>
        <div className="gb74-center">
          <h2 className="gb74-error-title">送信エラー</h2>
          <p>{submitError}</p>
          <button
            type="button"
            className="gb74-next-btn"
            disabled={isSubmitting}
            onClick={() => {
              if (failedAnswers) {
                handleSubmit(failedAnswers);
              }
            }}
          >
            {isSubmitting ? '送信中...' : '再送信する'}
          </button>
        </div>
      </Card>
    );
  }

  // 質問画面 - formatType別ルーティング
  const actualQuestion = sortedQuestions[currentQuestionIndex];

  if (flowState === 'answering' && actualQuestion) {
    const displayNumber = getDisplayNumber(currentQuestionIndex);
    const showBackButton = currentQuestionIndex > 0;
    const formatType = actualQuestion.formatType ?? 'binary';
    const currentPartNumber = partNumberOf(currentQuestionIndex);
    const currentAnswer = answers.find(a => a.questionId === actualQuestion.id);

    // === Scale UI (Part2/3/4/7) ===
    if (formatType === 'scale') {
      return (
        <Card>
          <ProgressHeader displayNumber={displayNumber} />
          <ScaleQuestionUI
            questionText={getLocalizedText(actualQuestion, i18n.language)}
            leadText={SCALE_LEAD_TEXTS[currentPartNumber]}
            questionNumber={displayNumber}
            totalQuestions={totalDisplayQuestions}
            selectedValue={currentAnswer?.scaleValue}
            onAnswer={handleScaleAnswer}
            onBack={handlePreviousQuestion}
            canGoBack={showBackButton}
          />
        </Card>
      );
    }

    // === Part5: ネームジェネレータ or ONA具体化 (会社単位設定で切替) ===
    if (formatType === 'choice') {
      // namegen モードかつ Part5 の O-11(相談人数) のみネームジェネレータに置換。
      // O-12(自律性)は従来の選択式のまま残す(基本方針: O-11のみ置換)。
      if (
        getPart5Mode() === 'namegen' &&
        currentPartNumber === 5 &&
        actualQuestion.questionCode === 'O-11'
      ) {
        return (
          <Card>
            <ProgressHeader displayNumber={displayNumber} />
            <NameGeneratorUI
              questionText={getLocalizedText(actualQuestion, i18n.language)}
              colleagues={namegenColleagues}
              selectedIds={namegenSelectedIds}
              maxSelect={3}
              onChange={setNamegenSelectedIds}
              onNext={handleNamegenNext}
              onBack={handlePreviousQuestion}
              canGoBack={showBackButton}
            />
          </Card>
        );
      }
      // ONA具体化 (現行)
      const sortedChoices = [...actualQuestion.answerChoices].sort((a, b) => a.order - b.order);
      return (
        <Card>
          <ProgressHeader displayNumber={displayNumber} />
          <ChoiceQuestionUI
            questionText={getLocalizedText(actualQuestion, i18n.language)}
            choices={sortedChoices.map(c => ({ id: c.id, text: getLocalizedText(c, i18n.language) }))}
            selectedChoiceId={currentAnswer?.answerChoiceId}
            onAnswer={handleChoiceAnswer}
            onBack={handlePreviousQuestion}
            canGoBack={showBackButton}
          />
        </Card>
      );
    }

    // === Matrix UI (Part6: 部門間マトリクス) ===
    if (formatType === 'matrix') {
      return (
        <Card>
          <ProgressHeader displayNumber={displayNumber} />
          <MatrixQuestionUI
            questionText={getLocalizedText(actualQuestion, i18n.language)}
            departments={departments}
            values={matrixValues}
            onChange={handleMatrixChange}
            onNext={handleMatrixNext}
            onBack={handlePreviousQuestion}
            canGoBack={showBackButton}
          />
        </Card>
      );
    }

    // === Binary UI (Part1) - 縦積み2択カード (モック s1) ===
    const sortedChoices = [...actualQuestion.answerChoices].sort((a, b) => a.order - b.order);
    const selectedChoiceId = currentAnswer?.answerChoiceId;

    return (
      <Card>
        <ProgressHeader displayNumber={displayNumber} />
        <h2 className="gb74-q-text">{getLocalizedText(actualQuestion, i18n.language)}</h2>
        {/* カウンターバランスはCSV焼き込み済みのため order どおり縦積み表示 (A/Bラベルなし) */}
        <ul className="gb74-binary" key={`question-list-${currentQuestionIndex}-${actualQuestion.id}`}>
          {sortedChoices.map((choice, index) => {
            const isSelected = selectedChoiceId === choice.id;
            return (
              <li key={`${currentQuestionIndex}-${actualQuestion.id}-${choice.id}-${index}`}>
                <button
                  id={`js-answer-0${index + 1}`}
                  type="button"
                  className={`gb74-binary-btn${isSelected ? ' sel' : ''}`}
                  disabled={isSubmitting}
                  onClick={() => handleChoiceAnswer(choice.id)}
                >
                  {getLocalizedText(choice, i18n.language)}
                </button>
              </li>
            );
          })}
        </ul>
        {showBackButton && (
          <p className="gb74-back">
            <button type="button" onClick={handlePreviousQuestion}>
              {t('questionnaire.previousQuestion')}
            </button>
          </p>
        )}
      </Card>
    );
  }

  // 完了画面 (モック s9)
  if (flowState === 'completed') {
    return (
      <Card>
        <div className="gb74-progress">
          <div className="gb74-progress-bar">
            <div className="gb74-progress-fill" style={{ width: '100%' }} />
          </div>
        </div>
        <div className="gb74-complete">
          <div className="gb74-complete-check">
            <svg width="28" height="28" viewBox="0 0 28 28">
              <path d="M7 14l5 5 9-9" fill="none" stroke="#3B6D11" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
          <h2 className="gb74-complete-title">
            {t('questionnaire.completionScreen.title', 'ご回答ありがとうございました。')}
          </h2>
          <p className="gb74-complete-note">
            {t('questionnaire.completionScreen.message', '皆さんの声をもとに、働きやすさの改善に取り組みます。')}
          </p>
          <p className="gb74-complete-safe">
            {t('questionnaire.completionScreen.safeNote', '誰が何と答えたかは、個人が特定されることはありません。')}
          </p>
        </div>
        <button type="button" className="gb74-next-btn outline" onClick={onNavigateToService}>
          {t('questionnaire.thanksScreen.toServiceTop')}
        </button>
        <button type="button" className="gb74-next-btn outline" onClick={onNavigateToMyPage}>
          {t('questionnaire.thanksScreen.toMyPage')}
        </button>
      </Card>
    );
  }

  return null;
};

export default DesignQuestionnaireFlow;
